// The sales side peek (spec 09 §4.15, D-314): a customer, a quote, an order or
// a deal beside the list it was opened from, on the register's `SidePeek`
// (design-system §3.12).
//
// ONE PANEL FOR THE SCREEN'S WHOLE LIFE. `SalesPeek` renders one `SidePeek`
// and swaps what is inside it, so switching from a quote to its customer from
// inside the panel keeps the panel, refocuses its heading and leaves focus
// return pointing at the control that first opened it (WP-E's primitive keys
// all of that off one mounted element). Each kind's read is a hook called with
// an empty id unless its kind is the one shown, which keeps it idle.
//
// EACH PEEK READS ITS RECORD'S EXISTING DETAIL GET and adds no route (D-314
// ruling 5): the customer 360's GET, the quote's, the order's and the deal's.
// The customer peek also reads the three lists the 360's Overview panels read
// (the customer's open deals, latest quotes and latest orders), five rows
// each, which are existing list routes filtered by the customer.
//
// WHAT A READER CANNOT OPEN. A 403 or a 404 reads "This record is not
// available to you" with Close, the primitive's unavailable state. Money
// follows the same field grant as the full page: a total the payload omits
// renders nothing, never a dash (§7). No contact address is painted in the
// customer peek, whatever the reader's grant (§4.15).
//
// ONE REVEAL (design-system §5; M09-S25 step 11 asserts it). The panel stays
// in its loading state until every read its body renders has answered: the
// record's GET, and then the customer's three lists, the open-task reads the
// tasks card asks (all, and mine for a reader who is not an admin), the stage
// names, the unit codes and, for an admin, the member directory. The panel
// asks those reads itself with the keys the body asks, so they are one
// request each, and the body mounts once over a warm cache. A sub-read that
// fails or waits offline still opens the panel: its own section then says so
// in one line, and none of them ever reads "Loading" for good. An admin's
// member directory held offline counts as answered, because its hook reports
// a paused read as pending (lib/reads.ts). The reveal latches per record: a
// sub-read asked again after it never takes the panel back to Loading.
//
// WHAT EACH KIND OFFERS is §4.15's list. The quote and order peeks' tasks
// offer Complete (with Add task), as the deal and customer peeks' do; Move to
// another day belongs to the order detail, the composer and the task list.
// A deal the reader cannot change carries no tasks card (subject-tasks.tsx).
//
// SWITCHING. The customer, deal, quote and order rows inside a peek carry
// their own Peek buttons (`peek.open(code)`), which replace the history entry
// (WP-E's `planPeekOpen`), so one Back closes the panel whichever record it
// shows. A task row peeks its most specific subject (`taskPeekCode`).

import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { RefLabel } from "../../../../../components/ref-label";
import {
  SidePeek,
  usePeekAddress,
  type PeekAddress,
  type SidePeekState,
} from "../../../../../components/side-peek";
import { peekCode, type PeekKind, type PeekTarget } from "../../../../../components/peek-address";
import { StatusChip } from "../../../../../components/status-chip";
import { useUoms } from "../../../catalogue/hooks/catalogue-queries";
import { formatBusinessDate } from "../../../../../lib/datetime";
import { RequestError, requestIdOf } from "../../../../../lib/request";
import { creditChipVariant, orderStatusVariant, tierChipVariant } from "../../lib/chips";
import { formatMinor } from "../../lib/money";
import { formatMonth, stageName } from "../../lib/pipeline";
import { orderPromisePending } from "../../lib/promise-dates";
import { readSettled, useMemberRefAsk, useReadsHeldOffline, useRevealLatch } from "../../lib/reads";
import { quoteStatusVariant } from "../../lib/quotes";
import { useSalesPermissions } from "../../hooks/permissions";
import { useCustomer360, useOwnerIndex, type Customer360 } from "../../hooks/sales-queries";
import { useActivities, useDocumentTasks, type TaskSubject } from "../../hooks/activity-queries";
import {
  useOrder,
  useOrders,
  useQuote,
  useQuotes,
  type OrderDetail,
  type QuoteDetail,
} from "../../hooks/quote-queries";
import {
  useOpportunities,
  useOpportunity,
  usePipelineStages,
  type OpportunityDetail,
} from "../../hooks/opportunity-queries";
import { MutationFailureNote } from "../../mutation-failure-note";
import { OpenTasksCard } from "../sales-tasks";
import { DealActions } from "../pipeline/deal-actions";
import { DealStatusChip, IdleChip, NextStepCell, StageText } from "../pipeline/pipeline-parts";
import { holderQuery, SubjectTasksCard, type TaskHolder } from "./subject-tasks";

/** How many rows each list inside the customer peek shows (§4.15). */
export const PEEK_LIST_LIMIT = 5;
/** How many lines the quote peek shows before pointing at the full page. */
export const PEEK_LINE_LIMIT = 5;

/** The customer peek's three lists, one query each, which the panel's reveal
 * gate and the body ask with the same keys. The latest orders are the newest
 * PLACED, as on the 360, so an order waiting for approval (no confirmation
 * date yet) is not pushed out of the five. */
export function peekCustomerQueries(customerId: string) {
  return {
    deals: { customer_id: customerId, limit: PEEK_LIST_LIMIT },
    quotes: { customer_id: customerId, limit: PEEK_LIST_LIMIT },
    orders: { customer_id: customerId, limit: PEEK_LIST_LIMIT, order: "-created_at" as const },
  };
}

/** The verbs a peek's task card offers (§4.15: Add task and Complete). */
const PEEK_TASK_VERBS = ["complete"] as const;

interface SettleLike {
  readonly data: unknown;
  readonly error: unknown;
  readonly fetchStatus: string;
}

const NO_HOLDER: TaskHolder = { kind: "customer", id: "", label: "" };
const NO_SUBJECT: TaskSubject = { kind: "quote", id: "", customerId: null };

/**
 * The sales lists' address half: `usePeekAddress` with the four records'
 * own pages as the phone landing (D-314 ruling 4).
 */
export function useSalesPeek(): PeekAddress {
  const navigate = useNavigate();
  return usePeekAddress({
    openRecord: (target: PeekTarget) => {
      switch (target.kind) {
        case "customer":
          void navigate({ to: "/sales/customers/$customerId", params: { customerId: target.id } });
          return;
        case "quote":
          void navigate({ to: "/sales/quotes/$quoteId", params: { quoteId: target.id } });
          return;
        case "order":
          void navigate({ to: "/sales/orders/$orderId", params: { orderId: target.id } });
          return;
        case "opportunity":
          void navigate({
            to: "/sales/opportunities/$opportunityId",
            params: { opportunityId: target.id },
          });
          return;
      }
    },
  });
}

/** A task row's peek: its most specific subject, which is its order, else its
 * quote, else its deal, else its customer (spec 09 §4.15, D-314 ruling 3). */
export function taskPeekCode(task: {
  readonly sales_order_id?: string | null | undefined;
  readonly quote_id?: string | null | undefined;
  readonly opportunity_id?: string | null | undefined;
  readonly customer_id?: string | null | undefined;
}): string | undefined {
  return (
    peekCode("order", task.sales_order_id) ??
    peekCode("quote", task.quote_id) ??
    peekCode("opportunity", task.opportunity_id) ??
    peekCode("customer", task.customer_id)
  );
}

/** The word for a peek's kind, the panel's name while it has no heading. */
export function peekKindLabel(t: (key: string) => string, kind: PeekKind): string {
  switch (kind) {
    case "customer":
      return t("sales.peek.kind.customer");
    case "quote":
      return t("sales.peek.kind.quote");
    case "order":
      return t("sales.peek.kind.order");
    case "opportunity":
      return t("sales.peek.kind.opportunity");
  }
}

interface ReadLike {
  readonly data: unknown;
  readonly error: unknown;
  readonly fetchStatus: string;
  readonly refetch: () => unknown;
}

/** A detail read, as the panel's state (pure, unit-tested). A 403 and a 404
 * are the same sentence: a record the reader cannot open. */
export function peekStateOf(
  read: ReadLike,
  words: {
    readonly loading: string;
    readonly notAvailable: string;
    readonly error: string;
    readonly retry: string;
    readonly requestId: string;
    readonly offlineTitle: string;
    readonly offlineBody: string;
  },
): SidePeekState {
  if (read.data !== undefined) return { status: "ready" };
  if (read.error instanceof RequestError) {
    if (read.error.httpStatus === 403 || read.error.httpStatus === 404) {
      return { status: "unavailable", message: words.notAvailable };
    }
  }
  if (read.error !== null && read.error !== undefined) {
    return {
      status: "error",
      title: words.error,
      retryLabel: words.retry,
      onRetry: () => {
        void read.refetch();
      },
      requestId: requestIdOf(read.error),
      requestIdLabel: words.requestId,
    };
  }
  if (read.fetchStatus === "paused") {
    return { status: "offline", title: words.offlineTitle, body: words.offlineBody };
  }
  return { status: "loading", label: words.loading };
}

/** A Peek button inside the panel, switching it to another record. */
function PeekSwitch({
  code,
  name,
  peek,
  label,
}: {
  readonly code: string | undefined;
  readonly name: string;
  readonly peek: PeekAddress;
  readonly label?: string | undefined;
}) {
  const { t } = useTranslation();
  if (code === undefined || !peek.offered) return null;
  return (
    <button
      type="button"
      className="ds-peek-btn"
      data-testid="sales-peek-switch"
      data-peek-key={code}
      aria-label={t("sales.peek.openNamed", { name })}
      onClick={() => {
        peek.open(code);
      }}
    >
      {label ?? t("sales.peek.open")}
    </button>
  );
}

function Fact({
  label,
  children,
  testId,
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly testId: string;
}) {
  return (
    <div className="sales-peek-fact" data-testid={testId}>
      <span className="sales-peek-fact-label">{label}</span>
      <span className="sales-peek-fact-value">{children}</span>
    </div>
  );
}

function OpenFull({ target }: { readonly target: PeekTarget }) {
  const { t } = useTranslation();
  const common = { className: "ds-lnk", "data-testid": "sales-peek-open-full" } as const;
  switch (target.kind) {
    case "customer":
      return (
        <Link to="/sales/customers/$customerId" params={{ customerId: target.id }} {...common}>
          {t("sales.peek.openFull")}
        </Link>
      );
    case "quote":
      return (
        <Link to="/sales/quotes/$quoteId" params={{ quoteId: target.id }} {...common}>
          {t("sales.peek.openFull")}
        </Link>
      );
    case "order":
      return (
        <Link to="/sales/orders/$orderId" params={{ orderId: target.id }} {...common}>
          {t("sales.peek.openFull")}
        </Link>
      );
    case "opportunity":
      return (
        <Link
          to="/sales/opportunities/$opportunityId"
          params={{ opportunityId: target.id }}
          {...common}
        >
          {t("sales.peek.openFull")}
        </Link>
      );
  }
}

/**
 * The screen's one side peek. Renders nothing while no peek is open (and
 * always on a phone, where `usePeekAddress` answers no target).
 */
export function SalesPeek({ peek }: { readonly peek: PeekAddress }) {
  if (peek.target === undefined || peek.code === undefined) return null;
  return <SalesPeekPanel peek={peek} target={peek.target} code={peek.code} />;
}

function SalesPeekPanel({
  peek,
  target,
  code,
}: {
  readonly peek: PeekAddress;
  readonly target: PeekTarget;
  readonly code: string;
}) {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  const customer = useCustomer360(target.kind === "customer" ? target.id : "");
  const quote = useQuote(target.kind === "quote" ? target.id : "");
  const order = useOrder(target.kind === "order" ? target.id : "");
  const deal = useOpportunity(target.id, target.kind === "opportunity");

  // ---- the reads the body renders, asked here with the body's own keys ----
  const canTasks = perms.has("sales.activity.write");
  const askMine = canTasks && !perms.isAdmin;
  const customerId =
    target.kind === "customer" && customer.data !== undefined ? customer.data.customer.id : "";
  const lists = peekCustomerQueries(customerId);
  const askLists = customerId.length > 0;
  const askDeals = askLists && perms.canOpenPipeline;
  const customerDeals = useOpportunities(lists.deals, undefined, askDeals);
  const customerQuotes = useQuotes(lists.quotes, undefined, askLists);
  const customerOrders = useOrders(lists.orders, undefined, askLists);
  const holder: TaskHolder | undefined =
    customerId.length > 0 && customer.data !== undefined
      ? { kind: "customer", id: customerId, label: customer.data.customer.display_name }
      : target.kind === "opportunity" && deal.data?.mutable === true
        ? {
            kind: "opportunity",
            id: deal.data.id,
            customerId: deal.data.customer_id,
            label: deal.data.opportunity_no,
          }
        : undefined;
  const askHolder = canTasks && holder !== undefined;
  const holderAll = useActivities(holderQuery(holder ?? NO_HOLDER, "all"), undefined, askHolder);
  const holderMine = useActivities(
    holderQuery(holder ?? NO_HOLDER, "mine"),
    undefined,
    askHolder && askMine,
  );
  const subject: TaskSubject | undefined =
    target.kind === "quote" && quote.data !== undefined
      ? { kind: "quote", id: quote.data.id, customerId: quote.data.customer_id }
      : target.kind === "order" && order.data !== undefined
        ? { kind: "order", id: order.data.id, customerId: order.data.customer_id }
        : undefined;
  const askSubject = canTasks && subject !== undefined;
  const subjectAll = useDocumentTasks(subject ?? NO_SUBJECT, "all", askSubject);
  const subjectMine = useDocumentTasks(subject ?? NO_SUBJECT, "mine", askSubject && askMine);
  const stages = usePipelineStages(target.kind === "opportunity" && perms.canOpenPipeline);
  const uoms = useUoms();
  const owners = useOwnerIndex(perms.isAdmin);
  // A directory read held offline reports pending until the network returns,
  // so it counts as settled while the browser is offline, as on the board.
  const offline = useReadsHeldOffline();
  const namesOwner = target.kind === "customer" || target.kind === "quote";
  const subReadsNow =
    readSettled(customerDeals, askDeals) &&
    readSettled(customerQuotes, askLists) &&
    readSettled(customerOrders, askLists) &&
    readSettled(holderAll, askHolder) &&
    readSettled(holderMine, askHolder && askMine) &&
    readSettled(subjectAll, askSubject) &&
    readSettled(subjectMine, askSubject && askMine) &&
    readSettled(stages, target.kind === "opportunity" && perms.canOpenPipeline) &&
    readSettled(uoms, target.kind === "quote" || target.kind === "order") &&
    (!namesOwner || !perms.isAdmin || !owners.isPending || offline);
  const read: ReadLike =
    target.kind === "customer"
      ? customer
      : target.kind === "quote"
        ? quote
        : target.kind === "order"
          ? order
          : deal;
  const primary = peekStateOf(read, {
    loading: t("sales.peek.loading"),
    notAvailable: t("sales.peek.notAvailable"),
    error: t("sales.peek.error"),
    retry: t("sales.common.retry"),
    requestId: t("sales.common.requestId"),
    offlineTitle: t("sales.offline.title"),
    offlineBody: t("sales.offline.body"),
  });
  // Once this record's panel has painted it stays painted: a sub-read asked
  // again (a failed one refetched on focus, the network returning) renders
  // in its own section, never as the whole panel's Loading. The latch closes
  // only once the record itself has answered. Every sub-read above is asked
  // from the record (its customer, its subject, its deal's `mutable`), so
  // while the record is still loading none of them is asked and they all
  // read as settled. A latch closed then would reveal the record the moment
  // it answered, with its task and list sections still loading inside the
  // painted panel (M09-S25 step 11).
  const subReadsSettled = useRevealLatch(primary.status === "ready" && subReadsNow, code);
  const state: SidePeekState =
    primary.status === "ready" && !subReadsSettled
      ? { status: "loading", label: t("sales.peek.loading") }
      : primary;
  const kind = peekKindLabel(t, target.kind);

  let name: string | undefined;
  let body: ReactNode = null;
  if (state.status !== "ready") {
    // Nothing of the record paints until the whole panel can.
  } else if (target.kind === "customer" && customer.data !== undefined) {
    name = customer.data.customer.display_name;
    body = <CustomerPeekBody data={customer.data} peek={peek} />;
  } else if (target.kind === "quote" && quote.data !== undefined) {
    name = quote.data.quote_no;
    body = <QuotePeekBody quote={quote.data} peek={peek} />;
  } else if (target.kind === "order" && order.data !== undefined) {
    name = order.data.order_no;
    body = <OrderPeekBody order={order.data} peek={peek} />;
  } else if (target.kind === "opportunity" && deal.data !== undefined) {
    name = deal.data.title;
    body = <DealPeekBody deal={deal.data} peek={peek} />;
  }

  return (
    <SidePeek
      peekKey={code}
      label={kind}
      heading={name === undefined ? undefined : t("sales.peek.title", { kind, name })}
      closeLabel={t("sales.peek.close")}
      onClose={peek.close}
      state={state}
    >
      {body}
      <p className="sales-peek-foot">
        <OpenFull target={target} />
      </p>
    </SidePeek>
  );
}

// ---------------------------------------------------------------------------
// Customer
// ---------------------------------------------------------------------------

function CustomerPeekBody({
  data,
  peek,
}: {
  readonly data: Customer360;
  readonly peek: PeekAddress;
}) {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  const owners = useOwnerIndex(perms.isAdmin);
  // Held offline, the directory read would show a resolving mark for the
  // whole cut; the owner reads as its short id until it answers.
  const offline = useReadsHeldOffline();
  const memberRef = useMemberRefAsk(owners.isPending && !offline);
  const customer = data.customer;
  const primary = data.contacts.find((c) => c.is_primary && c.status === "active");
  const lists = peekCustomerQueries(customer.id);
  const deals = useOpportunities(lists.deals, undefined, perms.canOpenPipeline);
  const quotes = useQuotes(lists.quotes, undefined);
  const orders = useOrders(lists.orders, undefined);

  return (
    <div className="sales-peek-body" data-testid="sales-peek-customer">
      <div className="sales-peek-facts">
        <Fact label={t("sales.customers.colNo")} testId="sales-peek-customer-no">
          <span className="num">{customer.customer_no}</span>
        </Fact>
        <Fact label={t("sales.peek.tier")} testId="sales-peek-customer-tier">
          <StatusChip variant={tierChipVariant(customer.tier)} dot={false}>
            {t(`sales.tier.${customer.tier}`)}
          </StatusChip>
        </Fact>
        <Fact label={t("sales.peek.status")} testId="sales-peek-customer-status">
          {customer.status === "active"
            ? t("sales.customers.statusActive")
            : t("sales.customers.statusArchived")}
        </Fact>
        {customer.credit_hold ? (
          <StatusChip variant="bad" data-testid="sales-peek-customer-credit-hold">
            {t("sales.customers.creditHold")}
          </StatusChip>
        ) : null}
        <Fact label={t("sales.peek.owner")} testId="sales-peek-customer-owner">
          {customer.owner_user_id === null ? (
            t("sales.peek.ownerNone")
          ) : (
            <RefLabel
              value={owners.byId.get(customer.owner_user_id)}
              id={customer.owner_user_id}
              pending={owners.isPending && !offline}
              {...memberRef}
            />
          )}
        </Fact>
        <Fact label={t("sales.peek.primaryContact")} testId="sales-peek-customer-contact">
          {primary?.full_name ?? t("sales.peek.primaryContactNone")}
        </Fact>
        <Fact label={t("sales.nextStep.label")} testId="sales-peek-customer-next">
          <NextStepCell next={customer.next_activity} needsNextStep={customer.needs_next_step} />
        </Fact>
      </div>

      <SubjectTasksCard
        holder={{ kind: "customer", id: customer.id, label: customer.display_name }}
        title={t("sales.peek.openTasks")}
        testId="sales-peek-tasks"
      />

      {perms.canOpenPipeline ? (
        <section className="sales-peek-section" data-testid="sales-peek-deals">
          <h3>{t("sales.peek.openDeals")}</h3>
          {deals.data === undefined ? (
            <SectionGap read={deals} what="deals" errorText={t("sales.c360.overview.dealsError")} />
          ) : deals.data.items.length === 0 ? (
            <p className="sales-hint" data-testid="sales-peek-deals-empty">
              {t("sales.peek.openDealsNone")}
            </p>
          ) : (
            <ul className="sales-peek-list">
              {deals.data.items.map((row) => (
                <li key={row.id} data-testid="sales-peek-deal-row" data-deal-id={row.id}>
                  <span className="sales-peek-list-main">{row.title}</span>
                  <span className="sales-hint num">{row.opportunity_no}</span>
                  <PeekSwitch code={peekCode("opportunity", row.id)} name={row.title} peek={peek} />
                </li>
              ))}
            </ul>
          )}
          {/* More open deals than the peek lists: the list filtered to the
              customer holds them all. */}
          {deals.data !== undefined && deals.data.next_cursor !== null ? (
            <Link
              to="/sales/opportunities"
              search={{ customer: customer.id }}
              className="ds-lnk"
              data-testid="sales-peek-deals-more"
            >
              {t("sales.c360.overview.dealsMore")}
            </Link>
          ) : null}
        </section>
      ) : null}

      <section className="sales-peek-section" data-testid="sales-peek-quotes">
        <h3>{t("sales.peek.latestQuotes")}</h3>
        {quotes.data === undefined ? (
          <SectionGap
            read={quotes}
            what="quotes"
            errorText={t("sales.c360.overview.quotesError")}
          />
        ) : quotes.data.items.length === 0 ? (
          <p className="sales-hint" data-testid="sales-peek-quotes-empty">
            {t("sales.peek.latestQuotesNone")}
          </p>
        ) : (
          <ul className="sales-peek-list">
            {quotes.data.items.map((row) => (
              <li key={row.id} data-testid="sales-peek-quote-row" data-quote-id={row.id}>
                <span className="sales-peek-list-main num">{row.quote_no}</span>
                <StatusChip variant={quoteStatusVariant(row.status)}>
                  {t(`sales.quoteStatus.${row.status}`)}
                </StatusChip>
                {perms.canOpenQuotes ? (
                  <PeekSwitch code={peekCode("quote", row.id)} name={row.quote_no} peek={peek} />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="sales-peek-section" data-testid="sales-peek-orders">
        <h3>{t("sales.peek.latestOrders")}</h3>
        {orders.data === undefined ? (
          <SectionGap
            read={orders}
            what="orders"
            errorText={t("sales.c360.overview.ordersError")}
          />
        ) : orders.data.items.length === 0 ? (
          <p className="sales-hint" data-testid="sales-peek-orders-empty">
            {t("sales.peek.latestOrdersNone")}
          </p>
        ) : (
          <ul className="sales-peek-list">
            {orders.data.items.map((row) => (
              <li key={row.id} data-testid="sales-peek-order-row" data-order-id={row.id}>
                <span className="sales-peek-list-main num">{row.order_no}</span>
                <StatusChip variant={orderStatusVariant(row.status)}>
                  {t(`sales.orders.status.${row.status}`)}
                </StatusChip>
                {perms.canOpenOrders ? (
                  <PeekSwitch code={peekCode("order", row.id)} name={row.order_no} peek={peek} />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quote
// ---------------------------------------------------------------------------

function useUomCodes(): ReadonlyMap<string, string> {
  const uoms = useUoms();
  const map = new Map<string, string>();
  for (const uom of uoms.data?.items ?? []) map.set(uom.id, uom.code);
  return map;
}

function QuotePeekBody({
  quote,
  peek,
}: {
  readonly quote: QuoteDetail;
  readonly peek: PeekAddress;
}) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const uoms = useUomCodes();
  // The owner's name comes from the member directory, which an admin reads;
  // anyone else sees the short id (no quote read serves a name, §4.15).
  const owners = useOwnerIndex(perms.isAdmin);
  const offline = useReadsHeldOffline();
  const memberRef = useMemberRefAsk(perms.isAdmin && owners.isPending && !offline);
  const lines = quote.revision.lines;
  const shown = lines.slice(0, PEEK_LINE_LIMIT);
  const total = quote.revision.total_minor;
  const deal = quote.opportunity;

  return (
    <div className="sales-peek-body" data-testid="sales-peek-quote">
      <div className="sales-peek-facts">
        <Fact label={t("sales.peek.status")} testId="sales-peek-quote-status">
          <StatusChip
            variant={quoteStatusVariant(quote.status)}
            data-testid="sales-quote-status"
            data-status={quote.status}
          >
            {t(`sales.quoteStatus.${quote.status}`)}
          </StatusChip>
          {quote.sent_revision_no !== null ? (
            <StatusChip
              variant={quote.first_viewed_at !== null ? "accent" : "neutral"}
              dot={quote.first_viewed_at !== null}
              data-testid="sales-peek-quote-viewed"
              data-viewed={quote.first_viewed_at !== null ? "true" : "false"}
            >
              {quote.first_viewed_at !== null
                ? t("sales.quotes.viewedYes")
                : t("sales.quotes.viewedNo")}
            </StatusChip>
          ) : null}
        </Fact>
        <Fact label={t("sales.peek.customer")} testId="sales-peek-quote-customer">
          {quote.customer_display_name}
          {perms.canOpenCustomers ? (
            <PeekSwitch
              code={peekCode("customer", quote.customer_id)}
              name={quote.customer_display_name}
              peek={peek}
              label={t("sales.peek.peekCustomer")}
            />
          ) : null}
        </Fact>
        {total !== undefined ? (
          <Fact label={t("sales.peek.total")} testId="sales-peek-quote-total">
            <span className="num">{formatMinor(total, quote.currency, i18n.language)}</span>
          </Fact>
        ) : null}
        <Fact label={t("sales.peek.validUntil")} testId="sales-peek-quote-valid">
          {quote.valid_until === null ? "" : formatBusinessDate(quote.valid_until, i18n.language)}
        </Fact>
        <Fact label={t("sales.peek.owner")} testId="sales-peek-quote-owner">
          <RefLabel
            value={owners.byId.get(quote.owner_user_id)}
            id={quote.owner_user_id}
            pending={perms.isAdmin && owners.isPending && !offline}
            {...memberRef}
          />
        </Fact>
        <Fact label={t("sales.quotes.colRevisions")} testId="sales-peek-quote-revision">
          {t("sales.peek.revision", { n: quote.current_revision_no })}
        </Fact>
        <Fact label={t("sales.peek.deal")} testId="sales-peek-quote-deal">
          {deal === null ? (
            // A deal writer is told the quote sits on a deal it cannot open
            // (#293, the detail's `opportunity_linked`).
            quote.opportunity_linked === true ? (
              t("sales.peek.dealHidden")
            ) : (
              t("sales.peek.dealNone")
            )
          ) : (
            <>
              <span data-testid="sales-peek-quote-deal-chip" data-deal-id={deal.id}>
                {deal.opportunity_no} · {deal.title}
              </span>
              <DealStatusChip status={deal.status} />
              {perms.canOpenPipeline ? (
                <PeekSwitch code={peekCode("opportunity", deal.id)} name={deal.title} peek={peek} />
              ) : null}
            </>
          )}
        </Fact>
      </div>

      <section className="sales-peek-section" data-testid="sales-peek-quote-lines">
        <h3>{t("sales.peek.lines")}</h3>
        <ul className="sales-peek-list">
          {shown.map((line) => (
            <li key={line.id} data-testid="sales-peek-quote-line">
              <span className="sales-peek-list-main">{line.description}</span>
              <span className="num">
                {line.qty} {uoms.get(line.uom_id) ?? ""}
              </span>
            </li>
          ))}
        </ul>
        {lines.length > shown.length ? (
          <p className="sales-hint" data-testid="sales-peek-quote-lines-more">
            {t("sales.peek.linesMore", { count: lines.length - shown.length })}
          </p>
        ) : null}
      </section>

      <OpenTasksCard
        subject={{ kind: "quote", id: quote.id, customerId: quote.customer_id }}
        label={quote.quote_no}
        verbs={PEEK_TASK_VERBS}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Order
// ---------------------------------------------------------------------------

function OrderPeekBody({
  order,
  peek,
}: {
  readonly order: OrderDetail;
  readonly peek: PeekAddress;
}) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const uoms = useUomCodes();
  return (
    <div className="sales-peek-body" data-testid="sales-peek-order">
      <div className="sales-peek-facts">
        <Fact label={t("sales.peek.status")} testId="sales-peek-order-status">
          <StatusChip
            variant={orderStatusVariant(order.status)}
            data-testid="sales-order-status"
            data-status={order.status}
          >
            {t(`sales.orders.status.${order.status}`)}
          </StatusChip>
        </Fact>
        <Fact label={t("sales.peek.customer")} testId="sales-peek-order-customer">
          {order.customer_display_name}
          {perms.canOpenCustomers ? (
            <PeekSwitch
              code={peekCode("customer", order.customer_id)}
              name={order.customer_display_name}
              peek={peek}
              label={t("sales.peek.peekCustomer")}
            />
          ) : null}
        </Fact>
        {order.total_minor !== undefined ? (
          <Fact label={t("sales.peek.total")} testId="sales-peek-order-total">
            <span className="num">
              {formatMinor(order.total_minor, order.currency, i18n.language)}
            </span>
          </Fact>
        ) : null}
        <Fact label={t("sales.peek.customerPo")} testId="sales-peek-order-po">
          {order.customer_po_ref ?? t("sales.peek.customerPoNone")}
        </Fact>
        <Fact label={t("sales.peek.credit")} testId="sales-peek-order-credit">
          <StatusChip
            variant={creditChipVariant(order.credit_check_status)}
            data-credit={order.credit_check_status}
          >
            {t(`sales.orders.credit.${order.credit_check_status}`)}
          </StatusChip>
        </Fact>
        {/* Module 03 owns the promise date: the peek names it, or says in
            one sentence that it is not set yet while planning can still set
            one (spec 09 §4.7, the order detail's rule). An undated shipped,
            delivered, closed or cancelled order gets no sentence. */}
        {order.promise_date !== null || orderPromisePending(order) ? (
          <p
            className="sales-hint"
            data-testid="sales-peek-order-promise"
            data-promise={order.promise_date ?? ""}
          >
            {order.promise_date === null
              ? t("sales.peek.promisePending")
              : t("sales.peek.promiseDate", {
                  date: formatBusinessDate(order.promise_date, i18n.language),
                })}
          </p>
        ) : null}
      </div>

      <section className="sales-peek-section" data-testid="sales-peek-order-lines">
        <h3>{t("sales.peek.lines")}</h3>
        <ul className="sales-peek-list">
          {order.lines.map((line) => (
            <li
              key={line.id}
              data-testid="sales-peek-order-line"
              data-line-status={line.line_status}
            >
              <span className="sales-peek-list-main">{line.description}</span>
              <span className="num">
                {t("sales.peek.shippedOf", {
                  shipped: line.qty_shipped,
                  ordered: line.qty_ordered,
                  uom: uoms.get(line.uom_id) ?? "",
                })}
              </span>
              {line.line_status === "cancelled" ? null : (
                <progress
                  className="sales-order-progress"
                  data-testid="sales-peek-order-progress"
                  value={line.qty_shipped}
                  max={line.qty_ordered}
                />
              )}
            </li>
          ))}
        </ul>
      </section>

      <OpenTasksCard
        subject={{ kind: "order", id: order.id, customerId: order.customer_id }}
        label={order.order_no}
        verbs={PEEK_TASK_VERBS}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deal
// ---------------------------------------------------------------------------

function DealPeekBody({
  deal,
  peek,
}: {
  readonly deal: OpportunityDetail;
  readonly peek: PeekAddress;
}) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const stages = usePipelineStages(perms.canOpenPipeline);
  const stageList = stages.data?.items ?? [];
  const stage = stageList.find((s) => s.id === deal.stage_id);
  const name = stage === undefined ? deal.stage_code : stageName(stage, i18n.language);

  return (
    <div className="sales-peek-body" data-testid="sales-peek-deal" data-deal-id={deal.id}>
      <div className="sales-peek-facts">
        <Fact label={t("sales.peek.kind.opportunity")} testId="sales-peek-deal-no">
          <span className="num">{deal.opportunity_no}</span>
          <DealStatusChip status={deal.status} />
        </Fact>
        <Fact label={t("sales.pipeline.deal.stage")} testId="sales-peek-deal-stage">
          <StageText name={name} pct={deal.probability_pct} />
          <IdleChip days={deal.idle_days} rotting={deal.rotting} />
        </Fact>
        <Fact label={t("sales.pipeline.deal.customer")} testId="sales-peek-deal-customer">
          <RefLabel value={deal.customer_name ?? undefined} id={deal.customer_id} />
          {perms.canOpenCustomers && deal.customer_name !== null ? (
            <PeekSwitch
              code={peekCode("customer", deal.customer_id)}
              name={deal.customer_name}
              peek={peek}
              label={t("sales.peek.peekCustomer")}
            />
          ) : null}
        </Fact>
        <Fact label={t("sales.pipeline.owner")} testId="sales-peek-deal-owner">
          {deal.owner_name ?? t("sales.pipeline.ownerNone")}
        </Fact>
        {deal.expected_value_minor !== undefined ? (
          <Fact label={t("sales.pipeline.deal.valueLabel")} testId="sales-peek-deal-value">
            {deal.expected_value_minor === null ? (
              t("sales.pipeline.noValue")
            ) : (
              <span className="num">
                {formatMinor(deal.expected_value_minor, deal.currency, i18n.language)}
              </span>
            )}
          </Fact>
        ) : null}
        <Fact label={t("sales.pipeline.deal.closeMonthLabel")} testId="sales-peek-deal-close">
          <span className={deal.close_month_passed ? "sales-valid-urgent" : undefined}>
            {formatMonth(deal.expected_close_month, i18n.language)}
          </span>
        </Fact>
        <Fact label={t("sales.pipeline.deal.nextStep")} testId="sales-peek-deal-next">
          <NextStepCell next={deal.next_activity} needsNextStep={deal.needs_next_step} />
        </Fact>
      </div>

      <DealActions deal={deal} stages={stageList} testPrefix="sales-peek-deal" />

      <section className="sales-peek-section" data-testid="sales-peek-deal-quotes">
        <h3>{t("sales.pipeline.deal.quotesTitle")}</h3>
        {deal.linked_quotes.length === 0 ? (
          <p className="sales-hint">{t("sales.pipeline.deal.quotesEmpty")}</p>
        ) : (
          <ul className="sales-peek-list">
            {deal.linked_quotes.map((row) => (
              <li key={row.id} data-testid="sales-peek-deal-quote" data-quote-id={row.id}>
                <span className="sales-peek-list-main num">{row.quote_no}</span>
                <StatusChip variant={quoteStatusVariant(row.status)}>
                  {t(`sales.quoteStatus.${row.status}`)}
                </StatusChip>
                {perms.canOpenQuotes ? (
                  <PeekSwitch code={peekCode("quote", row.id)} name={row.quote_no} peek={peek} />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <SubjectTasksCard
        holder={{
          kind: "opportunity",
          id: deal.id,
          customerId: deal.customer_id,
          label: deal.opportunity_no,
        }}
        title={t("sales.peek.openTasks")}
        testId="sales-peek-tasks"
        mutable={deal.mutable}
      />
    </div>
  );
}

/** A customer-peek list that has not answered: its own failure line with the
 * request id, the offline sentence, or (only while the panel is still gated,
 * which never paints it) nothing. */
function SectionGap({
  read,
  what,
  errorText,
}: {
  readonly read: SettleLike;
  readonly what: "deals" | "quotes" | "orders";
  readonly errorText: string;
}) {
  const { t } = useTranslation();
  if (read.error !== null && read.error !== undefined) {
    return (
      <MutationFailureNote
        data-testid={`sales-peek-${what}-error`}
        text={errorText}
        error={read.error}
      />
    );
  }
  if (read.fetchStatus === "paused") {
    return (
      <p className="sales-hint" role="status" data-testid={`sales-peek-${what}-offline`}>
        {t("sales.offline.body")}
      </p>
    );
  }
  return null;
}
